<template>
    <div v-if="isVisible" class="user-invoices-box box">
        <div class="box-title">
            Invoices
        </div>
        <div class="box-content">
            <transition
            name="fade"
            mode="out-in"
            >
                <div
                    v-if="isLoading"
                    class="loader"
                >
                    <i class="fas fa-spinner fa-spin loader" />
                </div>
                <template v-else>
                    <ul class="invoices-listing">
                        <template v-for="(invoice, index) in invoices">
                            <li v-if=" freePlan !== invoice.lines.data[0].plan.id" :key="index">
                                <div class="invoices-header">
                                    <div class="invoices-date">
                                        Date
                                    </div>
                                    <div class="invoices-amount">
                                        Amount
                                    </div>
                                    <div class="invoices-number">
                                        # of Shows
                                    </div>
                                    <div class="invoices-number">
                                        # of Hostings
                                    </div>
                                </div>
                                <div class="invoices-details">
                                    <div class="invoices-date">
                                        {{ dateExplicit( invoice.created ) }}
                                    </div>
                                    <div class="invoices-amount">
                                        {{ formatPrice( invoice.total ) }}
                                    </div>
                                    <div class="invoices-number">
                                        {{ invoiceShows( invoice ) }}
                                    </div>
                                    <div class="invoices-number">
                                        {{ invoiceHostings( invoice ) }}
                                    </div>
                                    <div class="invoices-download">
                                        <a class="functional-button" :href="`/api/billing/invoices/${invoice.id}/download`">
                                            <i class="fas fa-file-pdf" />
                                            <span>View invoice</span>
                                        </a>
                                    </div>
                                </div>
                            </li>
                        </template>
                    </ul>
                </template>
        </transition>
        </div>
    </div>
</template>

<script>
import { chain, reduce } from 'lodash';
import { mapState, mapActions } from 'vuex';
import usdFormatter from '@/utils/usdFormatter';
import moment from 'moment';
import toasts from '@/mixins/toasts';

export default {
    mixins: [toasts],

    data: () => ({
        freePlan: 'plan_H4cQhLOO9JbveX',
    }),

    computed: {
        ...mapState({
            invoices: state =>
                state.currentSubscription?.stripe_id && 'none' !== state.currentSubscription.name
                    ? state.invoices : null,
            products: state => state.products,
            isVisible: state => 'none' !== state.currentSubscription.stripe_id,
            isLoading: state => null === state.invoices || null === state.products,
            currentSubscription: state => state.currentSubscription,
        }),
    },

    watch: {
        currentSubscription: {
            immediate: true,

            /**
             * Watch changes on subscription
             *
             * @param {object} newSubscription New loaded subscription.
             * @param {object} oldSubscription Old loaded subscription.
             */
            handler(newSubscription, oldSubscription) {
                if ((oldSubscription?.stripe_id &&
                    (newSubscription.stripe_id !== oldSubscription?.stripe_id))
                ) {
                    this.loadUpcomingInvoice();
                    this.loadInvoices({ force: true });
                }
            },
        },
    },

    /**
     * Loads required data.
     */
    mounted() {
        this.$store.dispatch('loadProducts');
    },

    /**
     * Updated hook
     */
    updated() {
        if (false === this.invoices) {
            this.toastError({
                message: {
                    title: 'We could not import your invoices. Please try again later',
                },
            });
        }
    },

    methods: {
        ...mapActions([
            'loadInvoices',
            'loadUpcomingInvoice',
        ]),

        /**
         * Formats explicit date
         *
         * @param   { number }  date  Date timestamp
         * @returns  { string }  Formated date.
         */
        dateExplicit(date) {
            return moment.unix(date).format('MMM D, YYYY');
        },

        /**
         * Format price in USD
         *
         * @param  {number} price Price in cents.
         * @returns {string}       Formatted price.
         */
        formatPrice(price) {
            return usdFormatter(price);
        },

        /**
         * Counts shows in invoice.
         *
         * @param   {object} invoice Invoice data.
         * @returns {number}         Count of shows in invoice.
         */
        invoiceShows(invoice) {
            const prices = Object.values(
                chain(this.products).find({ type: 'plan' }).get('prices').value() || {}
            );

            return this.countInvoiceItems(invoice, prices);
        },

        /**
         * Counts hostings in invoice.
         *
         * @param   {object} invoice Invoice data.
         * @returns {number}         Count of hostings in invoice.
         */
        invoiceHostings(invoice) {
            const prices = Object.values(
                chain(this.products).find({ type: 'addon', 'add-on': 'hosting' }).get('prices').value() || {}
            );

            return this.countInvoiceItems(invoice, prices);
        },

        /**
         * Counts invoice items matching given products.
         *
         * @param   {object}   invoice Invoice data.
         * @param   {string[]} prices  List of product prices.
         * @returns {number}           Count of items.
         */
        countInvoiceItems(invoice, prices) {
            return reduce(invoice.lines.data, (count, lineItem) => {
                // do not count negative items
                if (0 >= lineItem.amount) {
                    return count;
                }

                if (! prices.includes(lineItem.price.id)) {
                    return count;
                }

                return count + lineItem.quantity;
            }, 0);
        },
    },
};
</script>
